import { cn, HelpText, Label } from '@atlas/ui-primitives'
import type { ComponentPropsWithRef, ReactNode } from 'react'

/**
 * Props for the {@link FieldShell} component.
 *
 * @property label - The field's visible label
 * @property htmlFor - `id` of the control this labels; also derives the error message's id
 * @property labelId - `id` for the label element, for a control that associates by `aria-labelledby`
 * @property span - Widens the cell across the responsive form grid
 * @property required - Renders the visual marker; see the accessibility note below
 * @property error - Validation message rendered under the control
 * @property description - Hint rendered under the control when there is no error
 * @property className - Optional additional Tailwind classes for the cell
 * @property children - The control this cell labels
 */
export interface FieldShellProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
	label: ReactNode
	htmlFor?: string
	labelId?: string
	span?: 2 | 3
	required?: boolean
	error?: string
	description?: ReactNode
	children: ReactNode
}

/**
 * The field-label treatment, for the few places that need it on an element
 * {@link FieldShell} cannot render: a group heading over several controls, or a
 * `dt` in a definition list. Anything labelling one control should use the
 * component instead of this string.
 */
export const FIELD_LABEL_CLASS = 'font-medium text-ds-xs uppercase tracking-wide text-ds-text-subtle'

const SPAN_CLASS = {
	2: 'sm:col-span-2',
	3: 'lg:col-span-3 sm:col-span-2',
} as const

/**
 * One labelled field cell: a label bound to the caller's control, an optional
 * required marker, and an optional description or validation message.
 *
 * @remarks
 * - **One label treatment.** The uppercase-subtle style the design system's own
 *   `SelectField` already used, in tokens only — no arbitrary size or tracking.
 *   `Field` and `SelectField` compose this cell, so the label element and its
 *   styling exist once.
 * - **The marker sits outside the label.** Inside, it becomes part of the label's
 *   accessible name — "Title *" rather than "Title" — for every assistive
 *   technology. It is `aria-hidden`, and its row is fixed-height because the `*`
 *   is taller than the label's mono text: without it a required field's control
 *   sits a pixel or two below an optional one sharing its grid row. This is also
 *   why the `Label` primitive's own `isRequired` is not used, which renders the
 *   marker inline.
 * - **The control owns its own state.** Required-ness and validity must reach
 *   assistive technology from the control (`isRequired`/`isInvalid` on a React
 *   Aria input, `required`/`aria-invalid` on a native one). This cell renders the
 *   *message*, not the state — it does not render the control and never modifies it.
 * - **A control that names itself by `aria-labelledby`** — a React Aria Combobox,
 *   say, which has no single element for `htmlFor` to point at — passes `labelId`
 *   and references it. The label element stays a real `<label>` either way.
 * - **`aria-describedby` is the caller's to wire.** Given `htmlFor`, the message
 *   carries `id="${htmlFor}-error"` and a description `id="${htmlFor}-description"`;
 *   point the control at whichever it needs. Children
 *   are never cloned, which would break React Aria components that manage their
 *   own descriptions.
 *
 * @example
 * ```tsx
 * <FieldShell label="Title" htmlFor="title" required error={errors.title}>
 *   <Input id="title" required aria-invalid={!!errors.title} aria-describedby="title-error" />
 * </FieldShell>
 * ```
 *
 * @see packages/ui-components/src/field-shell/field-shell.stories.tsx for visual examples
 */
export function FieldShell({
	label,
	htmlFor,
	labelId,
	span,
	required,
	error,
	description,
	className,
	children,
	...props
}: FieldShellProps) {
	const errorId = htmlFor && error ? `${htmlFor}-error` : undefined
	const descriptionId = htmlFor && description ? `${htmlFor}-description` : undefined
	return (
		<div {...props} className={cn(span && SPAN_CLASS[span], className)}>
			<div className="mb-1.5 flex h-4 items-center gap-1">
				<Label id={labelId} htmlFor={htmlFor} className={FIELD_LABEL_CLASS}>
					{label}
				</Label>
				{required ? (
					<span aria-hidden="true" className="text-ds-danger">
						*
					</span>
				) : null}
			</div>
			{children}
			{error ? (
				<HelpText id={errorId} role="alert" variant="error" className="mt-1.5">
					{error}
				</HelpText>
			) : description ? (
				<HelpText id={descriptionId} variant="hint" className="mt-1.5">
					{description}
				</HelpText>
			) : null}
		</div>
	)
}
